import Auth from '@aws-amplify/auth'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { ChevronLeft, MoreVert, Settings, Share } from '@material-ui/icons'
import { useLocation, useParams } from '@reach/router'
import { Link, navigate } from 'gatsby'
import { TFunction } from 'i18next'
import _ from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { errorToast } from '../../../../utils/errToast'
import { setItemToLocalStorage } from '../../../../utils/setLocalStorage'
import { TrackingCase } from '../../../../utils/trackingCases'
import { useAppState } from '../../../appState'
import birdAvatar from '../../../assets/images/bird-avatar.png'
import chirpyestIcon from '../../../assets/images/chirpyest-logo.svg'
import editIcon from '../../../assets/images/edit-info.svg'
import axios from '../../../axios'
import { ENDPOINTS, ROUTES } from '../../../constants'
import theme from '../../../gatsby-theme-material-ui-top-layout/theme'
import { tracker } from '../../../systemLogs'
import CHRButton from '../../button'
import { default as CHRInput, default as Input } from '../../input'
import CHRSectionContainer from '../../sectionContainer'
import BoardEmptyState from '../../shoppingBoard/boardEmptyState'
import PrivateFolderBadge from '../../shoppingBoard/privateFolderBadge'
import BoardProfile from '../../shoppingBoard/boardProfile'
import GridItemsStatic from '../../shoppingBoard/gridItemsStatic'
import GridPaging from '../../shoppingBoard/paging'
import SignInModal from '../../signInModal'
import CHRSpinner from '../../spinner'
import SettingsModal from '../settingsModal'
import CreateEditPageModal from '../createEditPageModal'
import { SbFolder } from '../settingsModal/type'
import SharePrivateFolderModal from '../shareModal'
import ContentContainer from './../../../components/contentContainer'
import { styles } from './styles'

interface ListingBoardProps {
  t: TFunction
  image: any
}

const ListingBoard = ({ t, image }: ListingBoardProps) => {
  const classes = styles()

  const [appState, dispatch] = useAppState()
  const [defaultSort, setDefaultSort] = useState('position')
  const [shoppingBoardItems, setShoppingBoardItems] = useState([])
  const [shoppingBoardError, setShoppingBoardError] = useState('')
  const [shareLink, setShareLink] = useState('')
  const [items, setItems] = useState([])
  const [canEdit, setEdit] = useState(false)
  // From the API, never inferred here — only the server knows whether it withheld this folder's
  // contents on privacy grounds, or whether this viewer is an admin.
  const [isAdminView, setIsAdminView] = useState(false)
  const [privacyRestricted, setPrivacyRestricted] = useState(false)
  const [updatePositionItems, setUpdatePositionItems] = useState([])
  const [userInfo, setUserInfo] = useState({})
  const [folderOrig, setFolderOrig] = useState({})
  const [folderInfo, setFolderInfo] = useState<SbFolder>({
    name: '',
    isPublic: false,
    isForwardable: false,
    isFeatured: false,
    totalFeatured: 0,
    displayImage: null,
    isDisplayImageVisible: true,
  })
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [isCreateEditPageModalOpen, setIsCreateEditPageModalOpen] = useState(
    false
  )

  const nameInput = useRef(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [folderNameError, setFolderNameError] = useState(false)
  const [isModal, setIsModal] = useState(false)
  const [sharePrivateFolderUrl, setSharePrivateFolderUrl] = useState<
    string | null
  >(null)
  const [sharePrivateFolderError, setSharePrivateFolderError] = useState('')
  const [isForwardFolderLoading, setIsForwardFolderLoading] = useState(false)
  const [forwardedFolderId, setForwardedFolderId] = useState(null)
  const [isOpenSignInModal, setIsOpenSignInModal] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null)

  const forwardedFolderLinkRef = useRef(null)

  const copyForwardedFolderLink = (event: any) => {
    if (forwardedFolderLinkRef && forwardedFolderLinkRef?.current !== null) {
      forwardedFolderLinkRef?.current?.select()
      document.execCommand('copy')
      event?.target?.focus()
      window.location.href = forwardedFolderLinkRef?.current?.value
    }
  }

  useEffect(() => {
    appState.userId && setIsLoggedIn(true)
  }, [])

  const params = useParams()
  const searchParams = new URLSearchParams(useLocation().search)

  const query = searchParams.get('t')
  const mode = searchParams.get('mode')

  const canGoBackToAllFolders = !mode || mode !== 'share'

  const options = [
    { value: 1, label: t('shoppingBoard.trending') },
    { value: 2, label: t('shoppingBoard.price') },
    { value: 3, label: t('shoppingBoard.name') },
    { value: 4, label: t('shoppingBoard.latest') },
    { value: 6, label: t('shoppingBoard.customPosition') },
    { value: 5, label: t('shoppingBoard.cashpack') },
  ]

  const nonUserOptions = [
    { value: 1, label: t('shoppingBoard.trending') },
    { value: 2, label: t('shoppingBoard.price') },
    { value: 3, label: t('shoppingBoard.name') },
    { value: 4, label: t('shoppingBoard.latest') },
    { value: 6, label: t('shoppingBoard.customPosition') },
  ]

  const [selectedSort, setSort] = useState(options[4])

  const [pages, setPages] = useState(0)
  const perPage = 48
  const [selectedPage, setSelectedPage] = useState(1)
  const [isLoadMore, setLoadMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const handleChangePage = (page: number) => {
    setLoadMore(false)
    setSelectedPage(page)
    window.scroll({
      top: 200,
      behavior: 'auto',
    })
  }

  const handleLoadMore = (page: number) => {
    setSelectedPage(page)
    setLoadMore(true)
  }

  const handleSortChange = (event: any) => {
    setSort(event)
    let newItems = shoppingBoardItems
    switch (event.value) {
      case 1: {
        newItems = _.orderBy(shoppingBoardItems, ['trendingCount'], ['desc'])
        break
      }
      case 2: {
        newItems = _.orderBy(
          shoppingBoardItems,
          item => Number(item.data.price.slice(1)),
          ['desc']
        )
        break
      }
      case 3: {
        newItems = _.orderBy(shoppingBoardItems, ['data.name'], ['asc'])
        break
      }
      case 4: {
        newItems = _.orderBy(shoppingBoardItems, ['id'], ['desc'])
        break
      }
      case 5: {
        newItems = _.orderBy(
          shoppingBoardItems,
          item =>
            Number(item.data.cashBack.slice(0, item.data.cashBack.length - 1)),
          ['desc']
        )
        break
      }
      case 6: {
        newItems = _.orderBy(
          shoppingBoardItems,
          ['folderItemPosition'],
          ['asc']
        )
        break
      }
      default:
        break
    }
    const itemsWithPaging = settingPages(newItems)
    setShoppingBoardItems(itemsWithPaging)
    setSelectedPage(1)
  }

  const handleRemoveItem = async (i: any) => {
    setItems(_.reject(items, { i: i }))
    tracker.track(
      TrackingCase.UserTracking,
      `DELETE ${ENDPOINTS.deleteItemFromFolder}`,
      { location: 'ListingBoard', itemId: i }
    )
    try {
      await axios.delete(
        ENDPOINTS.deleteItemFromFolder.replace(':folderItemId', i)
      )
      const newItems = _.reject(shoppingBoardItems, { folderItemId: i })
      const itemsWithPaging = settingPages(newItems)
      setShoppingBoardItems(itemsWithPaging)
      const oldPage = selectedPage
      const totalPages = _.ceil(itemsWithPaging.length / perPage)
      if (oldPage >= totalPages) {
        setSelectedPage(totalPages)
        setPages(totalPages)
      }
    } catch (error) {
      errorToast(`couldn't remove item, please try again`)
    }
  }

  const settingPages = (items: any) => {
    let newItems: any[] = []
    items.map((item: any, index: number) => {
      const num = index + 1
      const obj = item
      obj.page = _.ceil(num / perPage)
      obj.customPosition = num
      newItems.push({ obj })
    })
    return items
  }

  const [isMoved, setIsMoved] = useState(
    window.localStorage.getItem('movedItems') ?? false
  )

  const handleFolderUpdatePosition = async (
    itemId: number,
    itemPosition: number,
    targetIndex: any
  ) => {
    try {
      const newPosition = shoppingBoardItems[targetIndex].folderItemPosition
      if (newPosition !== itemPosition) {
        await axios.post(
          ENDPOINTS.SBFolderUpdatePosition.replace(
            ':folderId',
            params.folderId
          ),
          {
            id: itemId,
            newPosition: newPosition,
            currentPosition: itemPosition,
          }
        )

        setShoppingBoardItems(s => {
          let items
          if (newPosition > itemPosition) {
            items = shoppingBoardItems.map(item => ({
              ...item,
              folderItemPosition:
                item.folderItemId === itemId
                  ? newPosition
                  : item.folderItemPosition > itemPosition &&
                    item.folderItemPosition <= newPosition
                  ? item.folderItemPosition - 1
                  : item.folderItemPosition,
            }))
          } else {
            items = shoppingBoardItems.map(item => ({
              ...item,
              folderItemPosition:
                item.folderItemId === itemId
                  ? newPosition
                  : item.folderItemPosition >= newPosition &&
                    item.folderItemPosition < itemPosition
                  ? item.folderItemPosition + 1
                  : item.folderItemPosition,
            }))
          }
          return _.orderBy(items, ['folderItemPosition'], ['asc'])
        })
      }
    } catch (err) {
      errorToast(`couldn't update position, please try again`)
    }
  }

  const handleFolderUpdateProductImage = (id: number, imageUrl: string) => {
    const updated = shoppingBoardItems.map(item => {
      if (item.id === id) {
        return { ...item, data: { ...item.data, imageUrl } }
      }
      return item
    })
    setShoppingBoardItems(updated)
  }

  const reloadShopping = async () => {
    try {
      let { folderId } = params
      const endpoint = query
        ? `${ENDPOINTS.getSBFolder.replace(':id', folderId)}?t=${query}`
        : ENDPOINTS.getSBFolder.replace(':id', folderId)
      const currentUserInfo = await Auth.currentCredentials()
      // userId = await currentSession.getIdToken().payload['custom:user_id']
      const {
        data: { data },
      } = await axios.get(`${endpoint}`)
      const sbBoardItems = data || []
      setPages(sbBoardItems < 1 ? 0 : _.ceil(sbBoardItems.length / perPage))
      let trendingItems: any[] = []
      trendingItems = sbBoardItems
        ? _.orderBy(sbBoardItems, ['id'], ['desc'])
        : []
      setSort(options[4])
      const itemsWithPaging = settingPages(trendingItems)
      setShoppingBoardItems(itemsWithPaging)
      setSelectedPage(1)
    } catch (err) {
      // TODO: remove this function since it is not used anywhere or handle error if it is used in the future
    }
  }

  useEffect(() => {
    setIsLoading(true)
    const { userName, folderId } = params
    const endpoint = query
      ? `${ENDPOINTS.getSBFolder.replace(':id', folderId)}?t=${query}`
      : ENDPOINTS.getSBFolder.replace(':id', folderId)
    if (appState.username === userName) {
      setEdit(true)
    } else {
      setEdit(false)
    }
    const getShoppingBoard = async () => {
      try {
        const {
          data: { data },
        } = await axios.get(`${endpoint}`)
        data.folder?.shoppingBoardItems?.forEach((element: any) => {
          element['userId'] = data.userInfo?.id
        })
        const sbBoardItems = data.folder?.shoppingBoardItems || []
        setFolderOrig(data.folder)
        setUserInfo(data.userInfo)
        setIsAdminView(!!data.isAdminView)
        setPrivacyRestricted(!!data.privacyRestricted)
        setPages(sbBoardItems < 1 ? 0 : _.ceil(sbBoardItems.length / perPage))
        let trendingItems: any[] = []
        if (defaultSort === 'id') {
          trendingItems = sbBoardItems
            ? _.orderBy(sbBoardItems, ['id'], ['desc'])
            : []
        } else if (isMoved || defaultSort === 'position') {
          setSort(options[4])
          trendingItems = sbBoardItems
            ? _.orderBy(sbBoardItems, ['folderItemPosition'], ['asc'])
            : []
        } else {
          trendingItems = sbBoardItems
            ? _.orderBy(sbBoardItems, ['trendingCount'], ['desc'])
            : []
        }
        const itemsWithPaging = settingPages(trendingItems)
        setShoppingBoardItems(itemsWithPaging)
        setFolderInfo({
          name: data.folder?.name ? data.folder.name.toLowerCase() : '',
          isPublic: data.folder?.isPublic,
          isForwardable: data.folder?.isForwardable || false,
          isFeatured: data.folder?.isFeatured || false,
          totalFeatured: data.folder?.totalFeatured || 0,
          displayImage: data.folder?.displayImage || null,
          isDisplayImageVisible:
            data.folder?.isDisplayImageVisible !== null ||
            data.folder?.isDisplayImageVisible !== undefined
              ? data.folder?.isDisplayImageVisible
              : true,
        })
        setSelectedPage(1)
        setIsLoading(false)
      } catch (error) {
        if (error.response?.status === 404) {
          setIsLoading(false)
        } else {
          setIsLoading(false)
          setShoppingBoardError(t('messages.somethingWentWrong'))
        }
      }
    }

    getShoppingBoard()
  }, [dispatch, params.folderId, appState.username])

  useEffect(() => {
    if (updatePositionItems.length > 0) {
      var newItems = updatePositionItems
      updatePositionItems.map((item: any, index: number) => {
        if (item.status === 0) {
          try {
            ;(async () => {
              newItems[index]['status'] = 1
              setUpdatePositionItems(newItems)
              const {
                data: { data },
              } = await axios.put(ENDPOINTS.updatePositionShoppingBoardItem, {
                data: item.data,
              })
              if (data) {
                newItems[index]['status'] = -1
                setUpdatePositionItems(newItems)
              }
            })().catch(err => {})
          } catch (error) {
            // TODO: unused code, remove or handle error when used
          }
        }
      })
    }
  }, [updatePositionItems])

  const handleUpdateShortLink = (item, shortLink) => {
    setShoppingBoardItems(items => {
      const i = items.findIndex(e => e.id === item.id)
      return [
        ...items.slice(0, i),
        {
          ...items[i],
          data: {
            ...items[i].data,
            shortLink,
          },
        },
        ...items.slice(i + 1),
      ]
    })
  }

  const handleSBoardItemsChange = (item, action) => {
    const itemsCount =
      action === 'added'
        ? shoppingBoardItems.length + 1
        : shoppingBoardItems.length - 1
    setPages(itemsCount < 1 ? 0 : _.ceil(itemsCount / perPage))
    setSelectedPage(1)
    setShoppingBoardItems(pre => {
      const sbBoardItems = JSON.parse(JSON.stringify(pre))
      let trendingItems: any[] = []

      if (action === 'added') {
        sbBoardItems.push(item)
      } else {
        const index = sbBoardItems.findIndex(e => e.data.link === item.link)
        if (index > -1) {
          sbBoardItems.splice(index, 1)
        }
      }

      if (defaultSort === 'id') {
        trendingItems = sbBoardItems
          ? _.orderBy(sbBoardItems, ['id'], ['desc'])
          : []
      } else if (isMoved || defaultSort === 'position') {
        setSort(options[4])
        trendingItems = sbBoardItems
          ? _.orderBy(sbBoardItems, ['position'], ['asc'])
          : []
      } else {
        trendingItems = sbBoardItems
          ? _.orderBy(sbBoardItems, ['trendingCount'], ['desc'])
          : []
      }
      const itemsWithPaging = settingPages(trendingItems)
      return itemsWithPaging
    })
  }

  const focusNameInput = () => {
    setIsEditingName(true)
    nameInput.current.focus()
  }

  const updateFolderName = async () => {
    try {
      await axios.patch(ENDPOINTS.SBFolder.replace(':id', params.folderId), {
        name: folderInfo.name,
      })
    } catch (err) {
      setFolderNameError(t('messages.somethingWentWrong'))
      setTimeout(() => setFolderNameError(''), 1000)
    }
  }

  const closeModal = () => {
    setIsModal(false)
    setSharePrivateFolderError('')
  }

  const showShareUrl = async () => {
    try {
      setIsModal(true)

      const shareUrl = new URL(
        `${process.env.GATSBY_SITE_URL}/s/${
          params.userName
        }/${folderInfo.name.replace(/\s+|\/|\\|#/g, '-')}/${params.folderId}`
      )

      shareUrl.searchParams.set('mode', 'share')

      if (!folderInfo.isPublic) {
        tracker.track(
          TrackingCase.UserTracking,
          `POST ${ENDPOINTS.setFolderPrivateToken}`,
          { location: 'ListingBoard', folderId: params.folderId }
        )
        const {
          data: {
            data: { token },
          },
        } = await axios.post(
          ENDPOINTS.setFolderPrivateToken.replace(':id', params.folderId)
        )

        shareUrl.searchParams.set('t', token)
      }
      setSharePrivateFolderUrl(shareUrl.href)
    } catch (err) {
      setSharePrivateFolderError(t('messages.somethingWentWrong'))
    }
  }

  const forwardFolder = async () => {
    if (!isLoggedIn) {
      if (userInfo.username && userInfo.id) {
        setItemToLocalStorage(
          'referralId',
          JSON.stringify({
            id: `${userInfo.username}-${userInfo.id}`,
            time: new Date().toISOString(),
          })
        )
      }
      setIsOpenSignInModal(true)
      return
    }

    setIsForwardFolderLoading(true)

    const res = await axios.post(
      `${ENDPOINTS.SBFolderForwardBoard}`.replace(':folderId', params.folderId)
    )

    if (res.status === 200) {
      const folderId = res.data.data.folderId
      const username = res.data.data.username

      // NOTE: Uncomment if you want to show "copy and go" component
      // setForwardedFolderId(folderId)

      const navigateUrl = `${
        process.env.GATSBY_SITE_URL
      }/s/${username}/${folderInfo.name.replace(
        /\s+|\/|\\|#/g,
        '-'
      )}/${folderId}`

      navigate(navigateUrl)
    } else {
      toast.error('failed to forward board')
    }

    setIsForwardFolderLoading(false)
  }

  const closeSignInModal = () => {
    setIsOpenSignInModal(false)
  }

  const handleHideDisplayImage = async () => {
    try {
      await axios.patch(ENDPOINTS.SBFolder.replace(':id', params.folderId), {
        isDisplayImageVisible: false,
      })
      setFolderInfo(prev => ({
        ...prev,
        displayImage: null,
        isDisplayImageVisible: false,
      }))
      toast.success('image hidden')
    } catch (e) {
      toast.error('failed to change settings')
    }
  }

  if (isLoading) {
    return <CHRSpinner />
  }

  if (shoppingBoardError || _.isEmpty(userInfo)) {
    return (
      <CHRSectionContainer>
        <ContentContainer>
          <div className={classes.container404}>
            <img
              src={chirpyestIcon}
              alt={t('shared.chirpyest')}
              className={classes.logo}
            />
            <Typography variant="h1" align="center">
              {shoppingBoardError ? shoppingBoardError : t('notFound.title')}
            </Typography>
            <CHRButton
              label={t('notFound.buttonText')}
              onClick={() => navigate(ROUTES.home)}
              customStyle={classes.button404}
            />
          </div>
        </ContentContainer>
      </CHRSectionContainer>
    )
  }

  if (
    !folderInfo?.isPublic &&
    !canEdit &&
    !folderInfo.name &&
    !canGoBackToAllFolders
  ) {
    return (
      <>
        {!isSmallScreen || canEdit ? (
          <BoardProfile
            handleSortChange={handleSortChange}
            options={options}
            nonUserOptions={nonUserOptions}
            selectedSort={selectedSort}
            shareUrl={shareLink}
            canEdit={canEdit}
            reloadShopping={reloadShopping}
            shoppingUserProfile={{
              ...userInfo,
              imageKey: userInfo.image,
              imageUrl: '',
            }}
            handleSBoardItemsChange={handleSBoardItemsChange}
          />
        ) : null}
        <div className={classes.privateWrapper}>
          <Typography variant="h4">sorry, this board is private</Typography>
          <Typography variant="h3">
            you can view their{' '}
            <Link to={`/s/${params.userName}`} className={classes.shoppingLink}>
              other shopping boards here
            </Link>{' '}
            though!
          </Typography>
        </div>
      </>
    )
  }

  const isShoppingBoardEmpty =
    !shoppingBoardItems || shoppingBoardItems.length === 0

  return (
    <>
      {!isSmallScreen || canEdit ? (
        <BoardProfile
          handleSortChange={handleSortChange}
          options={options}
          nonUserOptions={nonUserOptions}
          selectedSort={selectedSort}
          shareUrl={shareLink}
          canEdit={canEdit}
          reloadShopping={reloadShopping}
          shoppingUserProfile={{
            ...userInfo,
            imageKey: userInfo.image,
            imageUrl: '',
          }}
          handleSBoardItemsChange={handleSBoardItemsChange}
        />
      ) : null}

      <ContentContainer customStyle={classes.container}>
        {canEdit && (
          <SettingsModal
            open={isSettingsModalOpen}
            folderId={params.folderId}
            username={params.userName}
            folder={folderInfo}
            onUpdate={setFolderInfo}
            onClose={() => setIsSettingsModalOpen(false)}
          />
        )}

        {canEdit && appState?.userProfile?.canCreateEditPage && (
          <CreateEditPageModal
            open={isCreateEditPageModalOpen}
            folderId={params.folderId}
            initialTitle={folderOrig?.name || folderInfo.name}
            itemCount={shoppingBoardItems.length}
            onClose={() => setIsCreateEditPageModalOpen(false)}
          />
        )}

        <div className={classes.wrapper}>
          <div className={classes.summaryItems}>
            {(!isShoppingBoardEmpty || folderInfo.displayImage) && (
              <>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems={canEdit ? 'flex-start' : 'flex-start'}
                  className={classes.subGrid}
                >
                  {!canEdit && isSmallScreen ? (
                    <div className={classes.subGridContainer}>
                      {canGoBackToAllFolders && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() =>
                            navigate(`/s/${params.userName}#folders`)
                          }
                          className={classes.backBtn}
                        >
                          <ChevronLeft fontSize="small" />
                          all folders
                        </Button>
                      )}

                      <Typography variant="h4" className={classes.folderName}>
                        {folderInfo.name}
                        <PrivateFolderBadge
                          isAdminView={isAdminView}
                          isPublic={folderInfo.isPublic}
                          className={classes.folderNameBadge}
                        />
                      </Typography>

                      <div className={classes.userInfoSection}>
                        <Typography
                          className={classes.userLink}
                          variant="subtitle1"
                        >
                          by{' '}
                          <div className={classes.profileImgContainer}>
                            <img
                              src={
                                userInfo.image
                                  ? `https://${process.env.GATSBY_S3_BUCKET_NAME}.s3.us-east-2.amazonaws.com/public/${userInfo.image}`
                                  : birdAvatar
                              }
                              className={classes.profileImg}
                              alt="profile-image"
                            />
                          </div>
                          <Link
                            style={{ display: 'block' }}
                            to={`/s/${params.userName}#folders`}
                          >
                            {userInfo.username}
                          </Link>
                        </Typography>

                        <Tooltip title="more options">
                          <IconButton
                            size="small"
                            color="primary"
                            component="div"
                            style={{ padding: 0 }}
                            onClick={event =>
                              setMoreMenuAnchorEl(event.currentTarget)
                            }
                          >
                            <MoreVert fontSize="small" htmlColor="dimgray" />
                          </IconButton>
                        </Tooltip>

                        <Menu
                          anchorEl={moreMenuAnchorEl}
                          open={Boolean(moreMenuAnchorEl)}
                          onClose={() => setMoreMenuAnchorEl(null)}
                        >
                          {folderInfo.isForwardable && !forwardedFolderId && (
                            <MenuItem
                              onClick={() => {
                                setMoreMenuAnchorEl(null)
                                forwardFolder()
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                style={{ margin: 0 }}
                              >
                                add this board
                              </Typography>
                            </MenuItem>
                          )}
                        </Menu>
                      </div>
                    </div>
                  ) : canEdit ? (
                    <div className={classes.titleActionsSection}>
                      <div className={classes.titleBlock}>
                        <div className={classes.inputNameWrapper}>
                          <Input
                            type="text"
                            value={folderInfo.name}
                            customStyles={`${classes.nameInput} ${
                              isEditingName ? classes.nameInputShow : ''
                            }`}
                            elementRef={nameInput}
                            onChange={(event: any) => {
                              setFolderInfo({
                                ...folderInfo,
                                name: event.target.value,
                              })
                            }}
                            onBlur={async () => {
                              if (!folderInfo.name)
                                setFolderNameError('name is required')
                              else {
                                setFolderNameError('')
                                setIsEditingName(false)
                                await updateFolderName()
                              }
                            }}
                          />

                          <div
                            className={`${classes.nameText} ${
                              isEditingName ? classes.nameTexthHide : ''
                            }`}
                          >
                            {folderInfo.name}
                          </div>
                          {folderInfo.name && (
                            <button
                              type="button"
                              className={`${classes.btnEdit} ${
                                isEditingName ? classes.btnEditHide : ''
                              }`}
                              onClick={focusNameInput}
                              aria-label="edit folder name"
                            >
                              {editIcon && (
                                <img
                                  src={editIcon}
                                  className={classes.btnIcon}
                                  alt=""
                                />
                              )}
                            </button>
                          )}
                        </div>
                        {folderNameError && (
                          <Typography
                            variant="button"
                            className={classes.nameValidation}
                          >
                            {folderNameError}
                          </Typography>
                        )}
                      </div>

                      <div className={classes.subBtns}>
                        <div className={classes.iconActions}>
                          <Tooltip
                            placement="top"
                            title="manage folder settings"
                          >
                            <Box className={classes.iconBtnWrap}>
                              <CHRButton
                                label={
                                  <Box className={classes.iconBtnLabel}>
                                    <Settings fontSize="small" />
                                  </Box>
                                }
                                onClick={() => setIsSettingsModalOpen(true)}
                                customStyle={classes.iconBtn}
                                hoverEffects={true}
                                aria-label="manage folder settings"
                              />
                            </Box>
                          </Tooltip>

                          <Tooltip placement="top" title="share the folder">
                            <Box className={classes.iconBtnWrap}>
                              <CHRButton
                                label={
                                  <Box className={classes.iconBtnLabel}>
                                    <Share fontSize="small" />
                                  </Box>
                                }
                                onClick={showShareUrl}
                                customStyle={classes.iconBtn}
                                hoverEffects={true}
                                aria-label="share the folder"
                              />
                            </Box>
                          </Tooltip>
                        </div>

                        {canGoBackToAllFolders && (
                          <CHRButton
                            label="back to all folders"
                            onClick={() =>
                              navigate(`/s/${params.userName}#folders`)
                            }
                            customStyle={classes.btn}
                            hoverEffects={true}
                          />
                        )}

                        {appState?.userProfile?.canCreateEditPage && (
                          <Tooltip
                            placement="top"
                            title={
                              shoppingBoardItems.length
                                ? 'create an edit page from this folder'
                                : 'add items to this folder before creating an edit page'
                            }
                          >
                            <Box className={classes.createEditPageBtnWrap}>
                              <CHRButton
                                label="create edit page"
                                onClick={() =>
                                  setIsCreateEditPageModalOpen(true)
                                }
                                customStyle={`${classes.btn} ${classes.btnOther}`}
                                hoverEffects={true}
                                disabled={!shoppingBoardItems.length}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <Typography variant="h4" className={classes.folderName}>
                        {folderInfo.name}
                        <PrivateFolderBadge
                          isAdminView={isAdminView}
                          isPublic={folderInfo.isPublic}
                          className={classes.folderNameBadge}
                        />
                      </Typography>

                      <div className={classes.subBtns}>
                        {canGoBackToAllFolders && !isSmallScreen && (
                          <CHRButton
                            label="back to all folders"
                            onClick={() =>
                              navigate(`/s/${params.userName}#folders`)
                            }
                            customStyle={classes.btn}
                            hoverEffects={true}
                          />
                        )}

                        {folderInfo.isForwardable &&
                          !forwardedFolderId &&
                          !isSmallScreen && (
                            <Tooltip
                              placement="top"
                              title="click to forward and copy this folder to your account"
                            >
                              <CHRButton
                                label="add this board"
                                onClick={forwardFolder}
                                customStyle={classes.btn}
                                hoverEffects={true}
                                isSubmitting={isForwardFolderLoading}
                              />
                            </Tooltip>
                          )}
                      </div>
                    </>
                  )}
                </Grid>

                {forwardedFolderId && (
                  <Box mt={2.5}>
                    <div className={`${classes.forwardedFolderLink}`}>
                      <CHRInput
                        placeholder=""
                        value={`${window.location.origin}/s/${
                          appState.username
                        }/${folderOrig.name.replace(
                          /\s+|\/|\\|#/g,
                          '-'
                        )}/${forwardedFolderId}`}
                        onChange={() => {}}
                        type="text"
                        elementRef={forwardedFolderLinkRef}
                      />
                      {document.queryCommandSupported('copy') && (
                        <CHRButton
                          label={'copy and go'}
                          onClick={copyForwardedFolderLink}
                        />
                      )}
                    </div>
                  </Box>
                )}

                <Typography
                  component="p"
                  className={classes.perItems}
                  style={{ marginTop: '2.5rem' }}
                >
                  {shoppingBoardItems.length} item
                  {shoppingBoardItems.length === 1 ? '' : 's'}
                </Typography>
                <Box className={classes.itemsWrapper}>
                  <GridItemsStatic
                    shoppingBoardItems={shoppingBoardItems}
                    handleRemoveItem={handleRemoveItem}
                    handleUpdateShortLink={handleUpdateShortLink}
                    handleUpdatePosition={handleFolderUpdatePosition}
                    handleUpdateProductImage={handleFolderUpdateProductImage}
                    canEdit={canEdit}
                    selectedPage={selectedPage}
                    isLoadMore={isLoadMore}
                    selectedSort={selectedSort}
                    isPositionSort={selectedSort?.value === 6}
                    isFolder={true}
                    displayImage={
                      folderInfo.isDisplayImageVisible
                        ? folderInfo.displayImage
                        : null
                    }
                    handleHideDisplayImage={handleHideDisplayImage}
                  />
                </Box>

                {!isShoppingBoardEmpty && (
                  <GridPaging
                    onChange={handleChangePage}
                    handleLoadMore={handleLoadMore}
                    selected={selectedPage}
                    pages={pages}
                  />
                )}
              </>
            )}

            {isShoppingBoardEmpty && !folderInfo.displayImage && (
              <BoardEmptyState
                canEdit={canEdit}
                hasUsername={!!appState.username}
                privacyRestricted={privacyRestricted}
              />
            )}
          </div>
        </div>

        <SharePrivateFolderModal
          isPublic={folderInfo.isPublic}
          openStatus={isModal}
          handleClose={closeModal}
          shareUrl={sharePrivateFolderUrl}
          error={sharePrivateFolderError}
        />

        <SignInModal
          handleClose={closeSignInModal}
          openStatus={isOpenSignInModal}
          setIsLoggedIn={setIsLoggedIn}
          allowRedirect={false}
        />
      </ContentContainer>
    </>
  )
}
export default withTranslation()(ListingBoard)
